const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/work/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/work/$id.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { resolveCatalogEntitySeo } from "@/lib/catalog-seo-policy";
import { WORK_RELEASE_URL_CONFIG, pickNumberArray, pickReleaseSearchFilter } from "@/lib/faceted-search-url";
import { jsonLdScripts } from "@/lib/jsonLdScripts";
import { withSiteTitle } from "@/lib/page-title";
import { pickEnum, pickNumber, pickString, pickStringArray } from "@/lib/search-params";
import { buildSeoMetaTags } from "@/lib/seo-meta";
import { formatWorkTitleWithDisambiguation } from "@/lib/work-title";
import { getRootLoaderData } from "@/types/intl";
const VALID_TABS = ["tracks"] as const;
type WorkSearch = {
  q?: string;
  format?: string;
  formatSet?: string[];
  sort?: string;
  country?: string;
  label?: string[];
  company?: string[];
  tag?: string[];
  year?: number[];
  filter?: ReleaseSearchFilter;
  tab?: (typeof VALID_TABS)[number];
  playing?: string;
  page?: number;
};
function buildWorkBreadcrumbItems(work: {
  artists?: Array<{
    id: string;
    name: string;
  }>;
  primaryIndex: {
    label: string;
    path: string;
  } | null;
  title: string;
}, id: string) {
  const items: Array<{
    name: string;
    url: string;
  }> = [];
  const currentWork = {
    name: work.title,
    url: `/work/${id}`
  };
  const artist = work.artists?.[0];
  if (work.primaryIndex) {
    items.push({
      name: work.primaryIndex.label,
      url: work.primaryIndex.path
    });
  }
  if (artist) items.push({
    name: artist.name,
    url: `/artist/${artist.id}`
  });
  items.push(currentWork);
  return items;
}
function pickReleasePage(value: unknown): number | undefined {
  const page = pickNumber(value);
  return page && Number.isInteger(page) && page > 1 ? page : undefined;
}
export const Route = createFileRoute("/_app/work/$id")({
  validateSearch: (raw: Record<string, unknown>): WorkSearch => ({
    q: pickString(raw.q),
    format: pickString(raw.format),
    formatSet: pickStringArray(raw.formatSet),
    sort: pickString(raw.sort),
    country: pickString(raw.country),
    label: pickStringArray(raw.label),
    company: pickStringArray(raw.company),
    tag: pickStringArray(raw.tag),
    year: pickNumberArray(raw.year),
    filter: pickReleaseSearchFilter(raw.filter, WORK_RELEASE_URL_CONFIG),
    tab: pickEnum(raw.tab, VALID_TABS),
    playing: pickString(raw.playing),
    page: pickReleasePage(raw.page)
  }),
  // Keep client-owned text search and pagination out of loaderDeps so they do
  // not create a new route match. The loader still reads them from
  // `location.search` for entry-time SSR prefetching. `playing` and `tab`
  // remain UI-only.
  loaderDeps: ({
    search
  }) => ({
    format: search.format,
    formatSet: search.formatSet,
    sort: search.sort,
    country: search.country,
    label: search.label,
    company: search.company,
    tag: search.tag,
    year: search.year,
    filter: search.filter
  }),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData,
    params
  }) => {
    const {
      locale,
      messages
    } = getRootLoaderData(match);
    const tMeta = createTranslator({
      locale,
      messages,
      namespace: "globals.metadata"
    });
    const tSeo = createTranslator({
      locale,
      messages,
      namespace: "globals.seo"
    });
    const tGenres = createTranslator({
      locale,
      messages,
      namespace: "shared.genres"
    });
    const tStyles = createTranslator({
      locale,
      messages,
      namespace: "shared.styles"
    });
    const work = loaderData?.work;
    if (!work) {
      return {
        meta: [{
          title: withSiteTitle(tMeta("work"))
        }]
      };
    }
    const {
      buildDefaultCommerceSeoFormat,
      buildWorkSeoDescription,
      buildWorkSeoTitle,
      formatSeoTaxonomyList
    } = await import("@/lib/seo");
    const {
      buildAlbumJsonLd,
      buildCatalogPageJsonLd
    } = await import("@/utils/jsonld");
    const entitySeo = resolveCatalogEntitySeo({
      policy: work.seo,
      currentEntityType: "work",
      currentCatalogId: work.id,
      locale
    });
    const displayTitle = formatWorkTitleWithDisambiguation(work.title, work.disambiguation);
    const formats = buildDefaultCommerceSeoFormat({
      tSeo,
      locale
    });
    const title = buildWorkSeoTitle({
      title: displayTitle,
      artist: work.artistDisplay,
      formats,
      year: work.year,
      tSeo
    });
    const tags = formatSeoTaxonomyList({
      genres: work.genres,
      styles: work.styles,
      translators: {
        genres: tGenres,
        styles: tStyles
      },
      locale
    });
    const description = buildWorkSeoDescription({
      title: displayTitle,
      artist: work.artistDisplay,
      formats,
      year: work.year,
      releaseCount: work.releaseCount,
      tags,
      tSeo,
      locale
    });
    const imageUrl = toAbsoluteUrl(work.imageUrl) ?? undefined;
    const url = entitySeo.canonicalUrl;
    const jsonLd = buildCatalogPageJsonLd({
      entity: work,
      emitStructuredData: entitySeo.emitStructuredData,
      locale,
      buildEntityJsonLd: work => buildAlbumJsonLd(work, displayTitle ? description : null),
      buildBreadcrumbItems: work => buildWorkBreadcrumbItems(work, params.id)
    });
    return {
      meta: buildSeoMetaTags({
        title,
        description,
        canonicalUrl: url,
        imageUrl
      }, {
        robots: entitySeo.robots,
        openGraph: {
          type: "music.album"
        },
        twitter: {}
      }),
      links: entitySeo.links,
      scripts: jsonLdScripts(jsonLd?.entity, jsonLd?.breadcrumb)
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});